import React from 'react';
import { CircularProgress, Box } from '@mui/material';
import { ICampaignResults, QuestionType, RatingScale } from '../../interface/ICampaign';
import { formatDate } from '../../utils/dateUtils';
import style from './CampaignResults.module.scss';

interface CampaignResultsProps {
	results?: ICampaignResults;
	onClose: () => void;
	onPickWinners?: () => void;
	isLoading?: boolean;
}

export default function CampaignResults({ results, onClose, onPickWinners, isLoading = false }: CampaignResultsProps): JSX.Element {
	if (isLoading || !results) {
		return (
			<div className={style.ResultsContainer}>
				<Box className={style.LoadingState}>
					<CircularProgress />
				</Box>
			</div>
		);
	}

	const { campaign, totalResponses, questionResults, winners } = results;

	// Calculate average rating across all rating questions
	const calculateOverallAverageRating = (): number | null => {
		const ratingQuestions = questionResults.filter(
			result => result.questionType !== QuestionType.YES_NO && result.averageRating !== undefined
		);
		
		if (ratingQuestions.length === 0) return null;
		
		const totalAverage = ratingQuestions.reduce((sum, result) => sum + (result.averageRating || 0), 0);
		return totalAverage / ratingQuestions.length;
	};

	const overallAverageRating = calculateOverallAverageRating();

	const getRatingLabel = (rating: number): string => {
		const labels = {
			[RatingScale.POOR]: 'Poor',
			[RatingScale.FAIR]: 'Fair',
			[RatingScale.AVERAGE]: 'Average',
			[RatingScale.GOOD]: 'Good',
			[RatingScale.EXCELLENT]: 'Excellent'
		};
		return labels[rating as RatingScale] || '';
	};

	// Check if campaign has ended (compare dates only, not time)
	const campaignHasEnded = () => {
		const now = new Date();
		const todayStr = now.toISOString().split('T')[0];
		const endDateStr = campaign.endDate.split('T')[0];
		return todayStr > endDateStr;
	};

	const canPickWinners =
		campaign.reward?.enabled &&
		campaign.reward?.numberOfWinners > 0 &&
		!winners?.length &&
		(results?.rewardOptInCount || 0) > 0 &&
		campaignHasEnded();
	console.log('Can Pick Winners:', winners);
	return (
		<div className={style.ResultsContainer}>
			<div className={style.Header}>
				<div className={style.HeaderTop}>
					<button onClick={onClose} className={style.BackButton}>
						Back
					</button>
					{campaign.reward?.enabled && onPickWinners && (
						<button 
							onClick={onPickWinners} 
							className={style.PickWinnerButton}
							disabled={!canPickWinners}
							title={!canPickWinners ? "Cannot pick winners yet" : "Pick winners randomly"}
							style={{ backgroundColor: '#446388', color: 'white' }}
						>
							Pick a Winner
						</button>
					)}
				</div>
				<h2>Campaign Results: {campaign.title}</h2>
			</div>

				<div className={style.CampaignSummary}>
					<div className={style.SummaryItem}>
						<span className={style.Label}>Start Date:</span>
						<span className={style.Value}>{formatDate(campaign.startDate, false)}</span>
					</div>
					<div className={style.SummaryItem}>
						<span className={style.Label}>End Date:</span>
						<span className={style.Value}>{formatDate(campaign.endDate, false)}</span>
					</div>
					<div className={style.SummaryItem}>
						<span className={style.Label}>Total Responses:</span>
						<span className={style.Value}>{totalResponses}</span>
					</div>
					<div className={style.SummaryItem}>
						<span className={style.Label}>Questions:</span>
						<span className={style.Value}>{campaign.questions.length}</span>
					</div>				{overallAverageRating !== null && (
					<div className={style.SummaryItem}>
						<span className={style.Label}>Score:</span>
						<span className={style.Value} style={{ color: '#446388', fontWeight: 'bold' }}>
							{overallAverageRating.toFixed(2)} / 5
						</span>
					</div>
				)}				</div>

				<div className={style.ResultsContent}>
					<h3>Question Results</h3>
					
					{questionResults.map((result, index) => (
						<div key={result.questionId} className={style.QuestionResult}>
							<div className={style.QuestionHeader}>
								<span className={style.QuestionNumber}>#{index + 1}</span>
								<span className={style.QuestionText}>{result.questionText}</span>
							</div>

							{result.questionType === QuestionType.YES_NO ? (
								<div className={style.YesNoResults}>
									<div className={style.ResultBar}>
										<div className={style.BarLabel}>
											<span>Yes</span>
											<span>{result.yesCount} ({result.yesPercentage?.toFixed(1)}%)</span>
										</div>
										<div className={style.BarContainer}>
											<div
												className={`${style.Bar} ${style.YesBar}`}
												style={{ width: `${result.yesPercentage}%` }}
											/>
										</div>
									</div>
									<div className={style.ResultBar}>
										<div className={style.BarLabel}>
											<span>No</span>
											<span>{result.noCount} ({result.noPercentage?.toFixed(1)}%)</span>
										</div>
										<div className={style.BarContainer}>
											<div
												className={`${style.Bar} ${style.NoBar}`}
												style={{ width: `${result.noPercentage}%` }}
											/>
										</div>
									</div>
								</div>
							) : (
								<div className={style.RatingResults}>
									<div className={style.AverageRating}>
										<span className={style.RatingScore}>{result.averageRating?.toFixed(2)}</span>
										<span className={style.RatingLabel}>Score</span>
									</div>
									<div className={style.RatingDistribution}>
										{[5, 4, 3, 2, 1].map((rating) => {
											const count = result.ratingDistribution?.[rating as RatingScale] || 0;
											const percentage = result.totalAnswers > 0 
												? (count / result.totalAnswers) * 100 
												: 0;

											return (
												<div key={rating} className={style.RatingRow}>
													<span className={style.RatingNumber}>{rating}</span>
													<span className={style.RatingText}>{getRatingLabel(rating)}</span>
													<div className={style.BarContainer}>
														<div
															className={`${style.Bar} ${style.RatingBar}`}
															style={{ width: `${percentage}%` }}
														/>
													</div>
													<span className={style.RatingCount}>
														{count} ({percentage.toFixed(1)}%)
													</span>
												</div>
											);
										})}
									</div>
								</div>
							)}
						</div>
					))}
				</div>

				{campaign.reward?.enabled && (
					<div className={style.RewardSection}>
						<h3>Reward Information</h3>
						<div className={style.RewardInfo}>
							<div className={style.RewardItem}>
								<span className={style.Label}>Reward:</span>
								<span className={style.Value}>{campaign.reward.rewardName}</span>
							</div>
							<div className={style.RewardItem}>
								<span className={style.Label}>Number of Winners:</span>
								<span className={style.Value}>{campaign.reward.numberOfWinners}</span>
							</div>
						</div>

						{winners && winners.length > 0 ? (
							<div className={style.WinnersList}>
								<h4>Contest Winners</h4>
								{winners.map((winner, index) => (
									<div key={winner.id} className={style.WinnerItem}>
										<span className={style.WinnerNumber}>#{index + 1}</span>
										<span className={style.WinnerName}>
											{winner.firstName && winner.lastName
												? `${winner.firstName} ${winner.lastName}`
												: 'Anonymous'}
										</span>
										{winner.email && <span className={style.WinnerEmail}>{winner.email}</span>}
									</div>
								))}
							</div>
						) : canPickWinners ? (
							<div className={style.PickWinnersSection}>
								<p>Campaign has ended. You can now select winners.</p>
								<button onClick={onPickWinners} className={style.PickWinnersButton}>
									Pick Winners Randomly
								</button>
							</div>
					) : !winners?.length && (results?.rewardOptInCount || 0) === 0 ? (
						<div className={style.NoWinnersMessage}>
							<p>No eligible submissions with reward opt-in yet.</p>
							<p>
								Eligible Submissions with Reward Opt-in: {results?.rewardOptInCount || 0}
							</p>
						</div>
					) : null}
				</div>
			)}
	</div>
	);
}
